import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PdpmEstimate,
  PdpmEstimateInput,
  PdpmFunctionBucket,
} from 'src/services/documentService/pdpmEstimate';
import PdpmInputEditor from './pdpm-input-editor';
import {
  ADVISORY_CHIP_TEXT,
  formatPoints,
  generalWarnings,
  groupInputsBySection,
  inputsForBucket,
  overrideCount,
  proximityHint,
  sourceChip,
  warningsForItem,
  warningSeverity,
} from './pdpm-estimator-utils';

/**
 * BRK-1968: the drawer showing every value the calculation consumed, where it
 * came from, and what it contributed. It renders the endpoint's `inputs[]` and
 * `derivation` — there is no FE-maintained field list, so an input the backend
 * adds shows up without a FE change (under "Other inputs" if the FE cannot place
 * it).
 *
 * BRK-1969: every row is editable as a per-session what-if. Overrides never
 * write back to clinical documents; they only re-run the estimate.
 */

/** Primary dx stays read-only in the MVP — free-text ICD what-ifs need backend validation UX. */
const READ_ONLY_ITEM_IDS = ['PRIMARY_DX'];

const DRAWER_TITLE_ID = 'pdpm-estimate-inputs-title';

export type PdpmEstimateDrawerProps = {
  open: boolean;
  onClose: () => void;
  estimate: PdpmEstimate | null;
  patientName?: string | null;
  ard: string | null;
  overrides: Record<string, string>;
  recalculating: boolean;
  onOverrideChange: (itemId: string, value: string) => void;
  onClearOverride: (itemId: string) => void;
  onResetOverrides: () => void;
};

export default function PdpmEstimateDrawer({
  open,
  onClose,
  estimate,
  patientName,
  ard,
  overrides,
  recalculating,
  onOverrideChange,
  onClearOverride,
  onResetOverrides,
}: PdpmEstimateDrawerProps) {
  const pendingOverrides = overrideCount(overrides);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // the drawer is a dialog; name it from its heading so screen readers
      // announce what opened (BRK-1971)
      PaperProps={{ 'aria-labelledby': DRAWER_TITLE_ID }}
    >
      <Box
        sx={{
          width: 480,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            gap={1}
          >
            <Stack sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                id={DRAWER_TITLE_ID}
              >
                PDPM Estimate Inputs
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {ADVISORY_CHIP_TEXT}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {[patientName, ard ? `ARD ${ard}` : null].filter(Boolean).join(' · ')}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              aria-label="Close PDPM estimate inputs"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Divider />

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, opacity: recalculating ? 0.6 : 1 }}>
          <DrawerContent
            estimate={estimate}
            overrides={overrides}
            recalculating={recalculating}
            onOverrideChange={onOverrideChange}
            onClearOverride={onClearOverride}
          />
        </Box>

        <Divider />
        {/* sticky footer: the blanket escape hatch back to documented values */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ p: 2 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexGrow: 1 }}
          >
            {pendingOverrides === 0
              ? 'No overrides'
              : `${pendingOverrides} override${pendingOverrides === 1 ? '' : 's'} applied`}
          </Typography>
          <Button
            size="small"
            onClick={onResetOverrides}
            disabled={pendingOverrides === 0}
          >
            Reset to documented values
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

function DrawerContent({
  estimate,
  overrides,
  recalculating,
  onOverrideChange,
  onClearOverride,
}: {
  estimate: PdpmEstimate | null;
  overrides: Record<string, string>;
  recalculating: boolean;
  onOverrideChange: (itemId: string, value: string) => void;
  onClearOverride: (itemId: string) => void;
}) {
  if (!estimate) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        No estimate loaded yet.
      </Typography>
    );
  }

  const inputs = estimate.inputs || [];
  if (inputs.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        This estimate reported no inputs.
      </Typography>
    );
  }

  const grouped = groupInputsBySection(inputs);
  const buckets = estimate.derivation?.buckets || [];
  const rowProps = { overrides, recalculating, onOverrideChange, onClearOverride, estimate };

  return (
    <Stack spacing={2}>
      {generalWarnings(estimate.warnings).map((warning, index) => (
        <Alert
          key={`${warning.code}-${index}`}
          severity={warningSeverity(warning.severity)}
        >
          {warning.message}
        </Alert>
      ))}

      {grouped.sections.map((section) => (
        <Stack
          key={section.section}
          spacing={1}
        >
          <Typography variant="subtitle2">{section.title}</Typography>

          {section.section === 'FUNCTION_SCORE' && buckets.length > 0 ? (
            <FunctionScoreSection
              buckets={buckets}
              sectionInputs={section.inputs}
              {...rowProps}
            />
          ) : (
            section.inputs.map((input) => (
              <InputRow
                key={input.itemId}
                input={input}
                showPoints={section.section === 'FUNCTION_SCORE'}
                {...rowProps}
              />
            ))
          )}

          {section.section === 'FUNCTION_SCORE' && <FunctionScoreFooter estimate={estimate} />}
          {section.section === 'SLP' && <SlpFooter estimate={estimate} />}
        </Stack>
      ))}

      {grouped.other.length > 0 && (
        <Stack spacing={1}>
          {/* rows the FE could not place — shown rather than silently dropped */}
          <Typography variant="subtitle2">Other inputs</Typography>
          {grouped.other.map((input) => (
            <InputRow
              key={input.itemId}
              input={input}
              {...rowProps}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function FunctionScoreSection({
  buckets,
  sectionInputs,
  ...rowProps
}: {
  buckets: PdpmFunctionBucket[];
  sectionInputs: PdpmEstimateInput[];
  estimate: PdpmEstimate;
  overrides: Record<string, string>;
  recalculating: boolean;
  onOverrideChange: (itemId: string, value: string) => void;
  onClearOverride: (itemId: string) => void;
}) {
  const bucketed = new Set(buckets.flatMap((bucket) => bucket.itemIds || []));
  // a GG row the buckets do not claim still has to render
  const unbucketed = sectionInputs.filter((input) => !bucketed.has(input.itemId));
  // a bucket whose itemIds match nothing we were sent would otherwise render as
  // a bare header with no rows under it
  const populated = buckets
    .map((bucket) => ({
      bucket,
      inputs: inputsForBucket(bucket.itemIds, rowProps.estimate.inputs),
    }))
    .filter(({ inputs }) => inputs.length > 0);

  return (
    <Stack spacing={1.5}>
      {populated.map(({ bucket, inputs }) => (
        <Stack
          key={bucket.bucket}
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
          >
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {bucket.label || bucket.bucket}
            </Typography>
            {bucket.rawAverage !== null && bucket.rawAverage !== undefined && (
              <Chip
                size="small"
                variant="outlined"
                label={`avg ${bucket.rawAverage}`}
              />
            )}
          </Stack>
          {inputs.map((input) => (
            <InputRow
              key={input.itemId}
              input={input}
              showPoints
              {...rowProps}
            />
          ))}
        </Stack>
      ))}
      {unbucketed.map((input) => (
        <InputRow
          key={input.itemId}
          input={input}
          showPoints
          {...rowProps}
        />
      ))}
    </Stack>
  );
}

function FunctionScoreFooter({ estimate }: { estimate: PdpmEstimate }) {
  const derivation = estimate.derivation;
  if (!derivation) {
    return null;
  }
  const hint = proximityHint(derivation);
  const parts = [
    derivation.functionScore !== null && derivation.functionScore !== undefined
      ? `Total ${derivation.functionScore}`
      : null,
    derivation.functionScoreBand ? `Band ${derivation.functionScoreBand}` : null,
  ].filter(Boolean);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      flexWrap="wrap"
    >
      {parts.length > 0 && <Typography variant="body2">{parts.join(' · ')}</Typography>}
      {hint && (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={hint}
        />
      )}
    </Stack>
  );
}

function SlpFooter({ estimate }: { estimate: PdpmEstimate }) {
  const slp = estimate.slpCmg;
  const parts = [
    slp?.criterionACount !== null && slp?.criterionACount !== undefined
      ? `Criterion A ${slp.criterionACount} of 3`
      : null,
    slp?.criterionBLevel ? `Criterion B ${slp.criterionBLevel}` : null,
  ].filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  return <Typography variant="body2">{parts.join(' · ')}</Typography>;
}

function InputRow({
  input,
  estimate,
  overrides,
  recalculating,
  showPoints,
  onOverrideChange,
  onClearOverride,
}: {
  input: PdpmEstimateInput;
  estimate: PdpmEstimate;
  overrides: Record<string, string>;
  recalculating: boolean;
  /** points only mean something where they feed the function score */
  showPoints?: boolean;
  onOverrideChange: (itemId: string, value: string) => void;
  onClearOverride: (itemId: string) => void;
}) {
  const chip = sourceChip(input);
  const overrideValue = overrides[input.itemId];
  const isPending = overrideValue !== undefined;
  const readOnly = READ_ONLY_ITEM_IDS.includes(input.itemId);
  const rowWarnings = warningsForItem(estimate.warnings, input.itemId);
  const derivedFrom = input.evidence?.derivedFrom;

  return (
    <Box
      // a row with a pending edit dims until the server echo lands, so the
      // numbers next to it are visibly not yet recomputed
      sx={{ opacity: isPending && recalculating ? 0.5 : 1, mb: 1.5 }}
    >
      {/* label and metadata on their own line so a long label wraps instead of
          truncating, and the editor below gets the full drawer width */}
      <Stack
        direction="row"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={0.5}
      >
        <Typography
          variant="body2"
          sx={{ flexGrow: 1, minWidth: '50%' }}
        >
          {input.label || input.itemId}
        </Typography>
        <Chip
          size="small"
          color={chip.color}
          variant={chip.variant}
          label={chip.label}
        />
        {showPoints && (
          <Chip
            size="small"
            variant="outlined"
            label={formatPoints(input)}
          />
        )}
        {isPending || input.source === 'OVERRIDE' ? (
          <Tooltip title="Clear this override">
            <IconButton
              size="small"
              aria-label={`Clear override for ${input.itemId}`}
              onClick={() => onClearOverride(input.itemId)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          // keeps the chips aligned with rows that do have a clear button
          <Box sx={{ width: 30, flexShrink: 0 }} />
        )}
      </Stack>

      <Box sx={{ mt: 0.5 }}>
        <PdpmInputEditor
          input={input}
          overrideValue={overrideValue}
          onOverrideChange={onOverrideChange}
          readOnly={readOnly}
        />
      </Box>

      {derivedFrom && derivedFrom.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Derived from {derivedFrom.join(', ')}
        </Typography>
      )}

      {rowWarnings.map((warning, index) => (
        <Alert
          key={`${warning.code}-${index}`}
          severity={warningSeverity(warning.severity)}
          sx={{ mt: 0.5 }}
        >
          {warning.message}
        </Alert>
      ))}
    </Box>
  );
}
